import { NODE_TYPES, EDGE_TYPES, TRIGGER_TYPES } from "constants/flowDesigner";

export const generateEdge = (sId, tId, type, isPositive) => {
  return {
    id: `${sId}->${tId}`,
    source: sId,
    target: tId,
    type: type || EDGE_TYPES.BUTTON,
    data: { isPositive },
    style: { opacity: 0 }
  };
};

export const generatePlaceholderSampleData = (sourceId, targetId) => {
  return {
    label: "",
    type: NODE_TYPES.PLACEHOLDER,
    customLabel: "",
    flowElement: {},
    expanded: true,
    source: sourceId,
    target: targetId,
    isBranch: true
  };
};

const getPlaceholderNodeFields = (position, id, data) => {
  return {
    id,
    position,
    data,
    type: NODE_TYPES.PLACEHOLDER,
    style: { opacity: 0 }
  };
};

export const addPlaceholderSteps = (params) => {
  const {
    position,
    sourceId,
    targetId,
    getId,
    countOfNodesToAdd = 1,
    isConditional = false
  } = params;
  const nodeData = generatePlaceholderSampleData(sourceId, targetId);
  const placeholderNodes = [];
  let placeholderEdges = [];

  for (let i = 0; i < countOfNodesToAdd; i++) {
    const phNodeId = getId();
    const incomingEdge = generateEdge(sourceId, phNodeId, true, isConditional && i === 0);
    const outgoingEdge = generateEdge(phNodeId, targetId, true);
    const newNode = getPlaceholderNodeFields(position, phNodeId, nodeData);

    placeholderNodes.push(newNode);

    placeholderEdges = [...placeholderEdges, ...[incomingEdge, outgoingEdge]];
  }

  return { placeholderNodes, placeholderEdges };
};

export const addLabelNodes = (params) => {
  const {
    position,
    sourceId,
    targetId,
    getId,
    countOfNodesToAdd = 1,
    branchName = "",
    isConditional = false
  } = params;
  const initialBranchNames = isConditional ? ["Then", "Else"] : ["Default", "Case 1", "Case 2"];
  const labelNodes = [];
  let labelEdges = [];
  let nodeData = getLabelNodeData(sourceId, targetId, sourceId);

  for (let i = 0; i < countOfNodesToAdd; i++) {
    const labelNodeId = getId();
    const label = branchName || initialBranchNames[i];
    const incomingEdge = generateEdge(
      sourceId,
      labelNodeId,
      EDGE_TYPES.LABEL,
      isConditional && i === 0
    );
    const outgoingEdge = generateEdge(labelNodeId, targetId);
    let newNode = {};

    newNode = getLabelNodeFields(position, labelNodeId, nodeData);
    newNode.data.label = label;
    newNode.data.labelParent = sourceId;
    labelNodes.push(newNode);

    labelEdges = [...labelEdges, ...[incomingEdge, outgoingEdge]];
  }

  return { labelNodes, labelEdges };
};

const getLabelNodeData = (sourceId, targetId, labelParent) => {
  return {
    label: "",
    type: NODE_TYPES.LABEL,
    customLabel: "",
    flowElement: {},
    expanded: true,
    source: sourceId,
    target: targetId,
    isBranch: true,
    labelParent
  };
};

const getLabelNodeFields = (position, id, data) => {
  return {
    id,
    position,
    data: { ...data },
    type: NODE_TYPES.LABEL,
    style: { opacity: 0 }
  };
};

export const getLogoURL = (logo) => {
  return logo ? (logo.indexOf("http") > -1 ? logo : `data:image/png;base64, ${logo}`) : "";
};

export const highlightNode = (nodeId, remarks) => {
  let node = document.querySelector(` .react-flow__node[data-id="${nodeId}"] .custom-node-wrapper`);

  if (node) {
    node.classList.remove("success-node", "failed-node");
    node.classList.add(remarks === "Success" ? "success-node" : "failed-node");
  }
};

export const clearNodesHighlight = () => {
  let nodes = document.querySelectorAll(`.react-flow__node .custom-node-wrapper`);
  nodes.forEach((node) => {
    node.classList.remove("success-node", "failed-node");
  });
};

export const openNewNodeProperties = (nodeId) => {
  setTimeout(() => {
    const flowElements = document.querySelectorAll(".react-flow.reactflow");
    flowElements.forEach((flowElement) => {
      if (flowElement.checkVisibility()) {
        const node = flowElement.querySelector(`#btn-node-${nodeId}`);
        if (node) {
          node.click();
        }
      }
    });
  }, 500);
};

export const contextCleanUp = (context, nodes, tenant_id) => {
  if (!context) return context;

  let workingContext = context;

  if (!workingContext.triggers) {
    workingContext.triggers = {};
  }

  const steps = workingContext.steps || {};
  Object.keys(steps).forEach((step) => {
    const isExist = nodes.some((n) => n.data.componentKey === step);
    steps[step].status = "";
    if (!isExist) {
      delete steps[step];
    }
  });

  if (workingContext.id) {
    workingContext.flow_id = workingContext.id;
    delete workingContext.id;
  }

  if (!workingContext.tenant_id && tenant_id) {
    workingContext.tenant_id = tenant_id;
  }

  return workingContext;
};

export const removeElementClassname = (element, classname) => {
  if (element) {
    element.classList.remove(classname);
  }
};

export const adjustTriggerCredentials = (triggers, context) => {
  triggers.forEach((trigger) => {
    const { properties, type } = trigger;
    const contextTriggerType = type === TRIGGER_TYPES.REST_ENDPOINT ? "rest" : "webhook";
    if (
      (type === TRIGGER_TYPES.REST_ENDPOINT || type === TRIGGER_TYPES.WEBHOOK) &&
      properties?.credentials?.required
    ) {
      const credentialsValueFromContext =
        context.triggers?.[contextTriggerType]?.credentials?.value;

      if (
        properties?.credentials.value === "basicAuth" ||
        properties?.credentials.value === "bearerToken"
      ) {
        trigger.properties.credentials.credentialType = properties?.credentials.value;
        trigger.properties.credentials.value = "";
      }

      if (!context.triggers) {
        context.triggers = {};
      }

      if (!context.triggers?.[contextTriggerType] && properties) {
        const { credentials, method, endpoint_url, respond, notes } = properties;
        context.triggers[contextTriggerType] = {
          method,
          endpoint_url,
          credentials: {
            required: credentials?.required,
            value: credentials?.value,
            credentialType: credentials?.credentialType,
            name: credentials?.name,
            username: credentials?.username,
            password: credentials?.password,
            token: credentials?.token
          },
          respond,
          notes
        };
      } else if (credentialsValueFromContext) {
        trigger.properties.credentials.value = credentialsValueFromContext;
      }
    }
  });

  Object.keys(context.variables).forEach((variable) => {
    const variableValue = context?.variables[variable];

    if (variableValue) {
      Object.keys(variableValue).forEach((key) => {
        if (variableValue[key] === undefined) {
          variableValue[key] = null;
        }
      });
    }
  });

  return { adjustedTriggers: triggers, adjustedContext: context };
};

export const isConnectorInFlow = (connector, flow) => {
  const flowConnectors = flow?.connectors || [];

  if (flowConnectors.length > 0) {
    return flowConnectors.some((flowConnector) => {
      const parsedConnector = JSON.parse(flowConnector);
      return parsedConnector.id === connector.id;
    });
  }

  return false;
};

export const normalizeContextPath = (value) => {
  return value.replace(/^@/, "")
};

export const isVariableValueEmpty = (value) => [undefined, null, ""].includes(value);
